import { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useAuth } from 'auth/AuthContext';
import { showAlert } from 'services/alerts.service';
import { makeRequest } from 'services/request.service';
import { getOnChangeFunction, getFormattedMissingFieldsMessage } from 'utils';
import ButtonComponent from 'components/common/ButtonComponent';
import LogoUploadComponent from 'components/common/LogoUploadComponent';
import LabeledSelectComponent from 'components/common/LabeledSelectComponent';
import LabeledInputComponent from 'components/common/LabeledInputComponent';
import SectionIntro from 'components/common/SectionIntro';
import { useTheme } from 'theme/ThemeContext';
import { useTranslation } from 'react-i18next';

const getSelectLogoOptions = t => [
  { value: '1', label: t('settings.reuseOptions.oneTime') },
  { value: '2', label: t('settings.reuseOptions.oneTimeShared') },
  { value: '3', label: t('settings.reuseOptions.regular') },
  { value: '4', label: t('settings.reuseOptions.shared') },
  { value: '5', label: t('settings.reuseOptions.units') },
  { value: '6', label: t('settings.reuseOptions.weightKg') }
];

const getSelectContainerTypeOptions = _t => [
  { value: '1', label: '89 PLASTIC/9.000KG 9.00 PLASTIC' }
];

const getSelectCreditDisplayOptions = t => [
  { value: '1', label: t('settings.creditDisplayOptions.units') },
  { value: '2', label: t('settings.creditDisplayOptions.weightKg') }
];

const FarmDetailsSection = ({ formData, onChangeValue, _user, missingFields }) => {
  const { t } = useTranslation();

  return (
    <section className="border border-gray-200 rounded-xl p-6 bg-gray-50 mb-6">
      <h2 className="font-semibold text-greenPrimary mb-4">{t('settings.farmDetails')}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <LabeledInputComponent
          label={t('form.firstName')}
          name="firstName"
          type="text"
          onChange={onChangeValue}
          defaultValue={formData.firstName}
          disabled
        />

        <LabeledInputComponent
          label={t('form.lastName')}
          name="lastName"
          type="text"
          defaultValue={formData.lastName}
          onChange={onChangeValue}
          disabled
        />

        <LabeledInputComponent
          label={t('settings.farmName')}
          name="farmName"
          type="text"
          value={formData.farmName}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        />

        <LabeledSelectComponent
          name="timezone"
          label={t('settings.timezone')}
          value={formData.timezone}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          {/* {Intl.supportedValuesOf('timeZone').map(tz => (
            <option key={tz} value={tz}>{tz}</option>
          ))} */}
          {[
            'America/Mexico_City',
            'UTC',
            'America/New_York',
            'America/Los_Angeles',
            'Europe/London',
            'Europe/Berlin',
            'Asia/Tokyo',
            'Asia/Shanghai',
            'Asia/Kolkata'
          ].map(tz => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </LabeledSelectComponent>

        <LabeledSelectComponent
          name="country"
          label={t('settings.country')}
          value={formData.country}
          onChange={onChangeValue}
        >
          <option value="Mexico">México</option>
          <option value="USA">Estados Unidos</option>
          <option value="Canada">Canadá</option>
        </LabeledSelectComponent>

        <LabeledInputComponent
          label={t('settings.costBaseHour')}
          name="costBaseHour"
          type="decimal"
          value={formData.costBaseHour}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        />

        <LabeledSelectComponent
          name="currency"
          label={t('settings.currency')}
          value={formData.currency}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          {['MXN', 'EUR', 'USD', 'ILS'].map(cr => {
            return (
              <option key={cr} value={cr}>
                {cr}
              </option>
            );
          })}
          {/* {
            Intl.supportedValuesOf('currency').map((cr) => {
              return <option key={cr} value={cr}>{cr}</option>
            })
          } */}
        </LabeledSelectComponent>

        {/* <div>
            <label className='block text-sm font-medium text-gray-700' htmlFor='definition-of-director-password'>Change Manage Protect Password *</label>
            <input
              type='password'
              id='definition-of-director-password'
              name='definitionOfDirectorPassword'
              value={formData.definitionOfDirectorPassword}
              onChange={onChangeValue}
              className='w-full mt-1 p-2 border rounded-md shadow-sm'
            />
          </div> */}

        <LabeledInputComponent
          label={t('settings.seasonStartDay')}
          name="seasonStartDay"
          type="number"
          value={formData.seasonStartDay}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        />

        <LabeledSelectComponent
          name="seasonStartMonth"
          label={t('settings.seasonStartMonth')}
          value={formData.seasonStartMonth}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          <option value="1">{t('common.months.january')}</option>
          <option value="2">{t('common.months.february')}</option>
          <option value="3">{t('common.months.march')}</option>
          <option value="4">{t('common.months.april')}</option>
          <option value="5">{t('common.months.may')}</option>
          <option value="6">{t('common.months.june')}</option>
          <option value="7">{t('common.months.july')}</option>
          <option value="8">{t('common.months.august')}</option>
          <option value="9">{t('common.months.september')}</option>
          <option value="10">{t('common.months.october')}</option>
          <option value="11">{t('common.months.november')}</option>
          <option value="12">{t('common.months.december')}</option>
        </LabeledSelectComponent>
      </div>
    </section>
  );
};

FarmDetailsSection.propTypes = {
  formData: PropTypes.object,
  onChangeValue: PropTypes.func,
  _user: PropTypes.object,
  missingFields: PropTypes.instanceOf(Set)
};

const WorkConfigurationSection = ({ formData, onChangeValue, missingFields }) => {
  const { t } = useTranslation();

  return (
    <section className="border border-gray-200 rounded-xl p-6 bg-gray-50 mb-6">
      <h2 className="font-semibold text-greenPrimary mb-4">{t('settings.workConfiguration')}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <LabeledSelectComponent
          label={t('settings.stretchingPolicy')}
          name="stretchingOfActivitiesPolicy"
          value={formData.stretchingOfActivitiesPolicy}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          <option value="1">{t('settings.stretchingPolicyOptions.forward')}</option>
          <option value="2">{t('settings.stretchingPolicyOptions.backwards')}</option>
          <option value="3">{t('settings.stretchingPolicyOptions.none')}</option>
        </LabeledSelectComponent>

        <LabeledSelectComponent
          label={t('settings.workdaySourcePolicy')}
          name="regulationsRelatedToTheWorkingDay"
          value={formData.regulationsRelatedToTheWorkingDay}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          <option value="1">{t('settings.workdaySourcePolicyOptions.methodA')}</option>
          <option value="2">{t('settings.workdaySourcePolicyOptions.methodB')}</option>
        </LabeledSelectComponent>

        <LabeledSelectComponent
          label={t('settings.roundToDefaultPolicy')}
          name="roundToDefaultPolicy"
          value={formData.roundToDefaultPolicy}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          <option value="1">{t('common.no')}</option>
          <option value="2">{t('common.yes')}</option>
        </LabeledSelectComponent>

        <LabeledSelectComponent
          label={t('settings.workdayActivityUploadPolicy')}
          name="workdayActivityUploadPolicy"
          value={formData.workdayActivityUploadPolicy}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          <option value="1">{t('settings.stretchingPolicyOptions.none')}</option>
          <option value="2">
            {t('settings.workdayActivityUploadPolicyOptions.startAndEndOnly')}
          </option>
        </LabeledSelectComponent>

        <LabeledInputComponent
          label={t('settings.startTime')}
          name="startTime"
          type="time"
          value={formData.startTime}
          onChange={onChangeValue}
        />

        <LabeledInputComponent
          label={t('settings.endTime')}
          name="endTime"
          type="time"
          value={formData.endTime}
          onChange={onChangeValue}
        />

        <LabeledInputComponent
          label={t('settings.breaksCounts')}
          name="numberOfBrakes"
          type="number"
          value={formData.numberOfBrakes}
          onChange={onChangeValue}
        />

        <LabeledInputComponent
          label={t('settings.breakStartTime')}
          name="brakeStartTime"
          type="time"
          value={formData.brakeStartTime}
          onChange={onChangeValue}
        />

        <LabeledInputComponent
          label={t('settings.breakEndTime')}
          name="brakeEndTime"
          type="time"
          value={formData.brakeEndTime}
          onChange={onChangeValue}
        />

        <LabeledInputComponent
          label={t('settings.irregularThreshold')}
          name="irregularThreshold"
          type="text"
          value={formData.irregularThreshold}
          onChange={onChangeValue}
        />

        <LabeledSelectComponent
          name="reuseConfiguration"
          label={t('settings.reuseConfiguration')}
          value={formData.reuseConfiguration}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          <option value="1">{t('settings.reuseOptions.regular')}</option>
          <option value="2">{t('settings.reuseOptions.shared')}</option>
        </LabeledSelectComponent>
      </div>
    </section>
  );
};

WorkConfigurationSection.propTypes = {
  formData: PropTypes.object,
  onChangeValue: PropTypes.func,
  missingFields: PropTypes.instanceOf(Set)
};

const LogoConfigurationSection = ({ formData, onChangeValue }) => {
  const { t } = useTranslation();
  const selectLogoOptions = getSelectLogoOptions(t);
  const selectContainerTypeOptions = getSelectContainerTypeOptions(t);
  const selectCreditDisplayOptions = getSelectCreditDisplayOptions(t);

  return (
    <section className="border border-gray-200 rounded-xl p-6 bg-gray-50">
      <h2 className="font-semibold text-greenPrimary mb-4">{t('settings.logoAndMiscellaneous')}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <LogoUploadComponent
          logo={formData.logo}
          onUpload={newLogo => onChangeValue({ target: { name: 'logo', value: newLogo } })}
        />

        <LabeledSelectComponent
          name="reuseSetting"
          label={t('settings.reuseSettings')}
          value={formData.reuseSetting}
          onChange={onChangeValue}
        >
          {selectLogoOptions.map(option => (
            <option key={option.value} value={option.label}>
              {option.label}
            </option>
          ))}
        </LabeledSelectComponent>

        <LabeledSelectComponent
          name="containerId"
          label={t('settings.containerTypeDefault')}
          value={formData.containerId}
          onChange={onChangeValue}
        >
          {selectContainerTypeOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </LabeledSelectComponent>

        <LabeledSelectComponent
          name="creditDisplayOption"
          label={t('settings.creditDisplayOption')}
          value={formData.creditDisplayOption}
          onChange={onChangeValue}
        >
          {selectCreditDisplayOptions.map(option => (
            <option key={option.value} value={option.label}>
              {option.label}
            </option>
          ))}
        </LabeledSelectComponent>
      </div>
    </section>
  );
};

const ThemePreferenceSection = () => {
  const { t } = useTranslation();
  const { themePreference, setThemePreference, resolvedTheme } = useTheme();

  const themeOptions = [
    {
      value: 'light',
      title: t('settings.themeLight'),
      description: t('settings.themeLightDescription')
    },
    {
      value: 'dark',
      title: t('settings.themeDark'),
      description: t('settings.themeDarkDescription')
    },
    {
      value: 'system',
      title: t('settings.themeSystem'),
      description: t('settings.themeSystemDescription')
    }
  ];

  return (
    <section className="border border-gray-200 rounded-xl p-6 bg-gray-50 mt-6">
      <div className="mb-4">
        <h2 className="font-semibold text-greenPrimary mb-1">{t('settings.appearance')}</h2>
        <p className="text-sm text-gray-500">
          {t('settings.appearanceHint')}{' '}
          {t(`settings.theme${resolvedTheme.charAt(0).toUpperCase() + resolvedTheme.slice(1)}`)}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {themeOptions.map(option => (
          <button
            key={option.value}
            type="button"
            onClick={() => setThemePreference(option.value)}
            className={`rounded-xl border p-4 text-left transition ${
              themePreference === option.value
                ? 'border-emerald-400/40 bg-emerald-400/10'
                : 'border-gray-200 bg-white hover:border-emerald-300/30'
            }`}
          >
            <p className="font-command-label text-[11px] uppercase tracking-[0.18em]">
              {option.title}
            </p>
            <p className="mt-2 text-sm text-gray-500">{option.description}</p>
          </button>
        ))}
      </div>
    </section>
  );
};

LogoConfigurationSection.propTypes = {
  formData: PropTypes.object,
  onChangeValue: PropTypes.func
};

const Settings = () => {
  const { t } = useTranslation();
  const hasFetched = useRef(false);
  const { user, setUser } = useAuth();

  const [state, setState] = useState({ formData: {}, missingFields: [] });

  const onChangeValue = getOnChangeFunction(setState, state.formData);

  const getGeneralSettings = async () => {
    const response = await makeRequest({
      method: 'get',
      url: `/management/general-settings/${user.userId}`,
      headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
    });

    if (response.status === 200) {
      setState(gs => ({
        ...gs,
        formData: response.data
      }));
    }
  };

  const createGeneralSettings = async data => {
    const result = await makeRequest({
      method: 'post',
      url: `/management/general-settings/${user.userId}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      },
      data,
      alertResult: true
    });

    if (result.status === 200) {
      setUser({ farmId: result.data.farm.farmId, ...user });
    }
  };

  const onSave = async e => {
    e.preventDefault();

    const requiredFields = [
      'farmName',
      'timezone',
      'costBaseHour',
      'currency',
      'seasonStartDay',
      'seasonStartMonth',
      'stretchingOfActivitiesPolicy',
      'regulationsRelatedToTheWorkingDay',
      'roundToDefaultPolicy',
      'workdayActivityUploadPolicy',
      'reuseConfiguration'
    ];

    const missingFields = requiredFields.filter(field => {
      const value = state.formData[field];
      return value === undefined || value === null || value === '';
    });

    setState(prev => ({ ...prev, missingFields }));

    if (missingFields.length > 0) {
      const htmlMessage = getFormattedMissingFieldsMessage(state.formData, requiredFields);
      showAlert({
        html: htmlMessage,
        icon: 'warning',
        focus: missingFields[0]
      });

      return;
    }

    await createGeneralSettings(state.formData);
  };

  useEffect(function () {
    if (hasFetched.current) return;
    hasFetched.current = true;

    getGeneralSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // const isFieldMissing = fieldName => state.missingFields.includes(fieldName)

  const missingFieldsSet = useMemo(() => new Set(state.missingFields), [state.missingFields]);

  return (
    <div className="min-h-screen p-6 text-slate-100">
      <form autoComplete="off">
        <SectionIntro
          kicker={t('pageIntro.managementKicker')}
          title={t('nav.generalSettings')}
          description={t('pageIntro.settingsDescription')}
          className="mb-6"
        />

        <hr className="workspace-summary-divider my-5 border-t" />

        <FarmDetailsSection
          formData={state.formData}
          onChangeValue={onChangeValue}
          user={user}
          missingFields={missingFieldsSet}
        />

        <WorkConfigurationSection
          formData={state.formData}
          onChangeValue={onChangeValue}
          missingFields={missingFieldsSet}
        />

        <LogoConfigurationSection formData={state.formData} onChangeValue={onChangeValue} />
        <ThemePreferenceSection />

        <div className="sticky bottom-0 mt-4 flex justify-end border border-gray-200 bg-white p-4 rounded-xl">
          <ButtonComponent onClick={onSave}>
            <PlusIcon className="mr-1 h-5 w-5" />
            {t('settings.saveGeneralSettings')}
          </ButtonComponent>
        </div>
      </form>
    </div>
  );
};

export default Settings;
